import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import Layout from "@/components/Layout";
import {
  ArrowLeft, Save, Plus, Trash2, Truck, Layers, Sparkles, Map as MapIcon,
} from "lucide-react";

const defaultPricing = {
  model: "tier",
  standard_tiers: [{ min_qty: 1, price_per_pallet: 0 }],
  large_tiers: [{ min_qty: 1, price_per_pallet: 0 }],
  half_pallet_price: 0,
  slot_booking_chf: 0,
  pallet_exchange_chf: 0,
  scheduled_delivery_chf: 0,
  second_delivery_chf: 0,
  second_delivery_free_count: 2,
  fuel_surcharge_pct: 0,
  storage_min_chf: 0,
  storage_max_chf: 0,
  large_packaging_codes: ["160x80"],
  evehicle_flat_chf: 0,
  evehicle_driver_payout_chf: 0,
};

const Section = ({ icon: Icon, title, children, action }) => (
  <div className="card p-5">
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-sm" style={{ background: "var(--accent-muted)" }}>
          <Icon size={16} strokeWidth={1.5} style={{ color: "var(--header)" }} />
        </div>
        <h2 className="text-base font-semibold">{title}</h2>
      </div>
      {action}
    </div>
    {children}
  </div>
);

const NumberField = ({ label, value, onChange, suffix, step = "0.01", testid }) => (
  <label className="block">
    <span className="label-eyebrow block mb-1">{label}</span>
    <div className="relative">
      <input
        type="number"
        min={0}
        step={step}
        data-testid={testid}
        value={value ?? 0}
        onChange={(e) => onChange(e.target.value === "" ? 0 : Number(e.target.value))}
        className="input-base font-mono pr-12"
      />
      {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[var(--text-3)]">{suffix}</span>}
    </div>
  </label>
);

const TierEditor = ({ title, tiers, setTiers, testidPrefix }) => {
  const add = () => setTiers([...tiers, { min_qty: (tiers[tiers.length - 1]?.min_qty || 0) + 1, price_per_pallet: 0 }]);
  const upd = (i, patch) => setTiers(tiers.map((t, idx) => idx === i ? { ...t, ...patch } : t));
  const del = (i) => setTiers(tiers.filter((_, idx) => idx !== i));
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm font-medium">{title}</div>
        <button type="button" onClick={add} data-testid={`${testidPrefix}-add-tier`} className="text-[11px] uppercase tracking-wider text-[#D4AF37] hover:underline flex items-center gap-1">
          <Plus size={12} /> Staffel-Stufe
        </button>
      </div>
      <div className="space-y-2">
        {tiers.map((t, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 items-center">
            <div className="col-span-3">
              <input
                type="number" min={1} step="1"
                className="input-base font-mono text-sm"
                value={t.min_qty}
                data-testid={`${testidPrefix}-tier-${i}-min`}
                onChange={(e) => upd(i, { min_qty: Number(e.target.value || 1) })}
                placeholder="ab"
              />
            </div>
            <div className="col-span-2 text-xs text-[var(--text-3)] text-center">Pal. →</div>
            <div className="col-span-5">
              <input
                type="number" min={0} step="0.01"
                className="input-base font-mono text-sm"
                value={t.price_per_pallet}
                data-testid={`${testidPrefix}-tier-${i}-price`}
                onChange={(e) => upd(i, { price_per_pallet: Number(e.target.value || 0) })}
                placeholder="CHF/Palette"
              />
            </div>
            <div className="col-span-2 text-right">
              {tiers.length > 1 && (
                <button type="button" onClick={() => del(i)} data-testid={`${testidPrefix}-tier-${i}-del`} className="text-[var(--text-3)] hover:text-red-700">
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const RoutesEditor = ({ customerId }) => {
  const [routes, setRoutes] = useState([]);
  const [draft, setDraft] = useState({ name: "", from_city: "", from_zip: "", to_city: "", to_zip: "", price_chf: 0 });
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get(`/customers/${customerId}/routes`);
      setRoutes(data);
    } catch (e) { setError(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [customerId]); // eslint-disable-line react-hooks/exhaustive-deps

  const add = async () => {
    if (!draft.name || !(draft.price_chf > 0)) { setError("Name und Preis erforderlich"); return; }
    try {
      await api.post(`/customers/${customerId}/routes`, draft);
      setDraft({ name: "", from_city: "", from_zip: "", to_city: "", to_zip: "", price_chf: 0 });
      setError("");
      load();
    } catch (e) { setError(formatApiError(e)); }
  };
  const del = async (id) => {
    if (!confirm("Strecke löschen?")) return;
    await api.delete(`/customers/${customerId}/routes/${id}`);
    load();
  };

  return (
    <div className="space-y-4">
      {error && <div className="border border-red-500/30 bg-red-500/5 text-red-700 text-xs px-3 py-2 rounded-sm">{error}</div>}
      <div className="grid grid-cols-12 gap-2 text-xs uppercase tracking-wider text-[var(--text-3)]">
        <div className="col-span-3">Name</div>
        <div className="col-span-2">PLZ Von</div>
        <div className="col-span-2">PLZ Nach</div>
        <div className="col-span-2">CHF</div>
        <div className="col-span-3 text-right">Aktion</div>
      </div>
      {routes.length === 0 && <div className="text-sm text-[var(--text-3)] text-center py-4">Noch keine Strecken erfasst.</div>}
      {routes.map(r => (
        <div key={r.id} className="grid grid-cols-12 gap-2 items-center pb-2 border-b border-[var(--border)]">
          <div className="col-span-3 text-sm">{r.name}</div>
          <div className="col-span-2 text-xs text-[var(--text-2)]">{r.from_zip} {r.from_city}</div>
          <div className="col-span-2 text-xs text-[var(--text-2)]">{r.to_zip} {r.to_city}</div>
          <div className="col-span-2 font-mono text-sm" style={{ color: "var(--header)" }}>{Number(r.price_chf).toFixed(2)}</div>
          <div className="col-span-3 text-right">
            <button onClick={() => del(r.id)} data-testid={`route-del-${r.id}`} className="text-[var(--text-3)] hover:text-red-700"><Trash2 size={14} /></button>
          </div>
        </div>
      ))}
      <div className="grid grid-cols-12 gap-2 items-center pt-3 border-t border-[var(--border)]">
        <input className="input-base text-sm col-span-3" placeholder="z.B. ZH → BE" value={draft.name} onChange={e => setDraft({ ...draft, name: e.target.value })} data-testid="route-draft-name" />
        <input className="input-base text-sm col-span-2 font-mono" placeholder="8000" value={draft.from_zip} onChange={e => setDraft({ ...draft, from_zip: e.target.value })} data-testid="route-draft-from-zip" />
        <input className="input-base text-sm col-span-2 font-mono" placeholder="3000" value={draft.to_zip} onChange={e => setDraft({ ...draft, to_zip: e.target.value })} data-testid="route-draft-to-zip" />
        <input type="number" min={0} step="0.01" className="input-base text-sm col-span-2 font-mono" value={draft.price_chf} onChange={e => setDraft({ ...draft, price_chf: Number(e.target.value || 0) })} data-testid="route-draft-price" />
        <button onClick={add} data-testid="route-add" className="col-span-3 btn-gold text-xs uppercase tracking-wider py-2 rounded-sm flex items-center justify-center gap-1"><Plus size={12} /> Hinzufügen</button>
      </div>
    </div>
  );
};

export default function CustomerPricing() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [pricing, setPricing] = useState(defaultPricing);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [cust, pr] = await Promise.all([
          api.get(`/customers`).then(r => r.data.find(c => c.id === id)),
          api.get(`/customers/${id}/pricing`).then(r => r.data),
        ]);
        setCustomer(cust);
        setPricing({ ...defaultPricing, ...pr });
      } catch (e) { setError(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, [id]);

  const save = async () => {
    setSaving(true); setError(""); setSuccess(false);
    try {
      await api.put(`/customers/${id}/pricing`, pricing);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 2500);
    } catch (e) { setError(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (loading) return <Layout><div className="p-10 text-center text-[var(--text-3)]">Lade…</div></Layout>;

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-6 md:py-10 space-y-6">
        <header className="flex flex-col md:flex-row md:items-end justify-between gap-3">
          <div>
            <button onClick={() => navigate(-1)} className="text-xs uppercase tracking-wider text-[var(--text-3)] hover:text-[#D4AF37] flex items-center gap-1 mb-2"><ArrowLeft size={12} /> Zurück</button>
            <div className="label-eyebrow" style={{ color: "var(--gold)" }}>Preiskonfiguration</div>
            <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">{customer?.name || "Kunde"}</h1>
          </div>
          <div className="flex items-center gap-3">
            {success && <span className="text-xs text-emerald-700 uppercase tracking-wider">✓ Gespeichert</span>}
            <button onClick={save} disabled={saving} data-testid="pricing-save" className="btn-gold px-5 py-3 rounded-sm text-sm uppercase tracking-wider flex items-center gap-2">
              <Save size={16} /> {saving ? "Speichern…" : "Speichern"}
            </button>
          </div>
        </header>

        {error && <div className="border border-red-500/30 bg-red-500/5 text-red-700 text-sm px-3 py-2 rounded-sm">{error}</div>}

        {/* Preismodell */}
        <Section icon={Layers} title="Preismodell">
          <div className="grid grid-cols-2 gap-3">
            {[
              { value: "tier", label: "Staffelpreise", desc: "Pro Palettentyp & Menge (wie SPC)" },
              { value: "flat", label: "Pauschalpreis", desc: "Pro Strecke oder manuell (wie Stähli/Sky)" },
            ].map(o => (
              <label
                key={o.value}
                data-testid={`pricing-model-${o.value}`}
                className={`flex flex-col gap-1 p-4 rounded-sm border cursor-pointer transition-colors ${
                  pricing.model === o.value
                    ? "border-[#D4AF37] bg-[#D4AF37]/10"
                    : "border-[var(--border)] hover:border-[var(--border-strong)]"
                }`}
              >
                <div className="flex items-center gap-2">
                  <input type="radio" checked={pricing.model === o.value} onChange={() => setPricing({ ...pricing, model: o.value })} className="accent-[#D4AF37]" />
                  <span className="font-medium text-sm">{o.label}</span>
                </div>
                <div className="text-xs text-[var(--text-2)] ml-6">{o.desc}</div>
              </label>
            ))}
          </div>
        </Section>

        {/* Staffel-Editor */}
        {pricing.model === "tier" && (
          <Section icon={Truck} title="Staffelpreise · Standard- & Grosspaletten">
            <div className="grid md:grid-cols-2 gap-6">
              <TierEditor
                title="Standardpalette (120×80×160)"
                tiers={pricing.standard_tiers || []}
                setTiers={(v) => setPricing({ ...pricing, standard_tiers: v })}
                testidPrefix="standard"
              />
              <TierEditor
                title="Grosspalette (160×80×160)"
                tiers={pricing.large_tiers || []}
                setTiers={(v) => setPricing({ ...pricing, large_tiers: v })}
                testidPrefix="large"
              />
            </div>
            <div className="mt-6 grid md:grid-cols-2 gap-3 pt-4 border-t border-[var(--border)]">
              <NumberField label="½ Palette (CHF/Stück)" value={pricing.half_pallet_price} onChange={(v) => setPricing({ ...pricing, half_pallet_price: v })} suffix="CHF" testid="half-pallet-price" />
            </div>
          </Section>
        )}

        {/* Pauschalpreis-Strecken */}
        {pricing.model === "flat" && (
          <Section icon={MapIcon} title="Strecken · Pauschalpreise">
            <div className="text-xs text-[var(--text-2)] mb-3">
              Beim Auftrag wählt der Admin die passende Strecke per Dropdown. Der Preis kann pro Auftrag manuell überschrieben werden.
            </div>
            <RoutesEditor customerId={id} />
          </Section>
        )}

        {/* Zusatzleistungen */}
        <Section icon={Sparkles} title="Zusatzleistungen">
          <div className="grid md:grid-cols-3 gap-3">
            <NumberField label="Slot-Buchung" value={pricing.slot_booking_chf} onChange={(v) => setPricing({ ...pricing, slot_booking_chf: v })} suffix="CHF/Stk" testid="slot-booking" />
            <NumberField label="Palettentausch" value={pricing.pallet_exchange_chf} onChange={(v) => setPricing({ ...pricing, pallet_exchange_chf: v })} suffix="CHF/Pal" testid="pallet-exchange" />
            <NumberField label="Lieferung auf Termin" value={pricing.scheduled_delivery_chf} onChange={(v) => setPricing({ ...pricing, scheduled_delivery_chf: v })} suffix="CHF" testid="scheduled-delivery" />
            <NumberField label="Zweitzustellung ab Pal." value={pricing.second_delivery_chf} onChange={(v) => setPricing({ ...pricing, second_delivery_chf: v })} suffix="CHF/Pal" testid="second-delivery" />
            <NumberField label="Davon gratis (Stk.)" value={pricing.second_delivery_free_count} onChange={(v) => setPricing({ ...pricing, second_delivery_free_count: v })} suffix="Stk" step="1" testid="second-delivery-free" />
            <NumberField label="Treibstoffzuschlag" value={pricing.fuel_surcharge_pct} onChange={(v) => setPricing({ ...pricing, fuel_surcharge_pct: v })} suffix="%" step="0.1" testid="fuel-pct" />
            <NumberField label="Zwischenlagerung min." value={pricing.storage_min_chf} onChange={(v) => setPricing({ ...pricing, storage_min_chf: v })} suffix="CHF" testid="storage-min" />
            <NumberField label="Zwischenlagerung max." value={pricing.storage_max_chf} onChange={(v) => setPricing({ ...pricing, storage_max_chf: v })} suffix="CHF" testid="storage-max" />
            <NumberField label="E-Fahrzeug Pauschale (Kunde)" value={pricing.evehicle_flat_chf} onChange={(v) => setPricing({ ...pricing, evehicle_flat_chf: v })} suffix="CHF/Auftrag" testid="evehicle-flat" />
            <NumberField label="E-Fahrzeug Fahrer-Vergütung" value={pricing.evehicle_driver_payout_chf} onChange={(v) => setPricing({ ...pricing, evehicle_driver_payout_chf: v })} suffix="CHF/Auftrag" testid="evehicle-driver-payout" />
          </div>
        </Section>
      </div>
    </Layout>
  );
}
